import { useMemo } from "react";
import { useCategoryItems } from "@/modules/dropdowns/hooks/useDropdowns";
import type { CompanyDetail, CompanyListItem } from "./types";

/**
 * Phase 1+2 visibility helper.
 *
 * Single source of truth for "which tabs and which stat cards should appear
 * on the Company detail page". Driven by:
 *   - `company.category` (Operational vs External) — from Phase 3.
 *   - `company.types`' dropdown metadata.systemCode — also from Phase 3
 *     (e.g. CLIENT/CUSTOMER/VENDOR/INVESTOR carry a systemCode like 'client',
 *     'vendor'. PARENT/SUBSIDIARY/PARTNER and any user-added types have none.)
 *     A company can now hold multiple types — the visible tab set is the
 *     union across all of them.
 *
 * Operational companies retain the full pre-Phase-1+2 tab set (no regression).
 * External companies get a focused, reference-oriented set keyed off systemCode.
 *
 * Do NOT duplicate this logic — both CompanyDetailPage and the stat-cards row
 * consume it. If a future tab needs gating, add it here.
 */

export type CompanyTabKey =
  | "overview"
  | "subsidiaries"
  | "products"
  | "projects"
  | "meetings"
  | "tasks"
  | "actions"
  | "notes"
  | "ideas"
  | "milestones"
  | "decisions"
  | "budgets"
  | "expenses"
  | "contacts"
  | "proposals"
  | "team"
  | "documents"
  | "timeline";

export type SummaryCardKey =
  | "products"
  | "projects"
  | "meetings"
  | "tasks"
  | "actions"
  | "expenses"
  | "contacts"
  | "proposals"
  | "team";

const OPERATIONAL_TABS: CompanyTabKey[] = [
  "overview",
  "subsidiaries",
  "products",
  "projects",
  "meetings",
  "tasks",
  "actions",
  "notes",
  "ideas",
  "milestones",
  "decisions",
  "budgets",
  "expenses",
  "team",
  "documents",
  "timeline",
];

const OPERATIONAL_CARDS: SummaryCardKey[] = [
  "products",
  "projects",
  "meetings",
  "tasks",
  "actions",
  "expenses",
  "team",
];

export type CompanyVisibility = {
  systemCodes: string[];
  isExternal: boolean;
  visibleTabs: CompanyTabKey[];
  visibleCards: SummaryCardKey[];
};

export function useCompanyVisibility(
  company: Pick<CompanyDetail | CompanyListItem, "category" | "types"> | null | undefined,
): CompanyVisibility {
  const { data: typeOptions = [] } = useCategoryItems("company_type");

  return useMemo<CompanyVisibility>(() => {
    if (!company) {
      return {
        systemCodes: [],
        isExternal: false,
        visibleTabs: [],
        visibleCards: [],
      };
    }

    if (company.category === "OPERATIONAL") {
      return {
        systemCodes: [],
        isExternal: false,
        visibleTabs: OPERATIONAL_TABS,
        visibleCards: OPERATIONAL_CARDS,
      };
    }

    // EXTERNAL — collect systemCodes from every type the company holds,
    // then union their entitlements.
    const systemCodes: string[] = [];
    for (const typeValue of company.types) {
      const row = typeOptions.find((t) => t.value === typeValue);
      const meta = row?.metadata;
      const sc =
        meta && typeof meta === "object" && typeof (meta as Record<string, unknown>).systemCode === "string"
          ? ((meta as Record<string, unknown>).systemCode as string)
          : null;
      if (sc && !systemCodes.includes(sc)) systemCodes.push(sc);
    }

    const isClientLike =
      systemCodes.includes("client") || systemCodes.includes("customer");
    const isVendor = systemCodes.includes("vendor");

    const tabs: CompanyTabKey[] = ["overview", "contacts", "meetings"];
    if (isClientLike) tabs.push("projects", "proposals");
    if (isVendor) tabs.push("expenses");
    // Documents tab visible for all External companies — they legitimately
    // have associated documents (contracts, specs, etc.) per Phase 4 design.
    tabs.push("documents");
    tabs.push("timeline");

    const cards: SummaryCardKey[] = ["contacts", "meetings"];
    if (isClientLike) cards.push("projects", "proposals");
    if (isVendor) cards.push("expenses");

    return {
      systemCodes,
      isExternal: true,
      visibleTabs: tabs,
      visibleCards: cards,
    };
  }, [company, typeOptions]);
}
